import { useEffect, useRef } from 'react';

interface Props {
  /** Grid spacing in CSS px. Default 24. */
  spacing?: number;
  /** Dot radius in CSS px. Default 1.5. */
  dotSize?: number;
  /** Cyan base color of the dots. Default neon cyan to match KNKT brand. */
  cyan?: string;
  /** Accent color for every Nth dot. Default neon copper. */
  accent?: string;
  /** Every Nth (i+j)%n === 0 dot uses the accent color. Default 7. */
  accentEvery?: number;
  /** Animation step per frame. Higher = faster. Default 0.03. */
  speed?: number;
}

/**
 * Animated dot-grid background. Ported from KNKT-Promo's CanvasBackground
 * so the auth-shell visual stays consistent across products.
 *
 *   • Fixed-position canvas, viewport-sized, zero pointer events.
 *   • Hi-DPI aware (devicePixelRatio scaling), so dots stay crisp on retina.
 *   • Pauses on `prefers-reduced-motion` — renders a single static frame.
 *
 * Drop it in once at the page root; siblings render above it.
 */
export default function DotsBackground({
  spacing = 24,
  dotSize = 1.5,
  cyan = '0,240,255',
  accent = '255,107,53',
  accentEvery = 7,
  speed = 0.03,
}: Props) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 2));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    let progress = 0;
    let raf = 0;

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      ctx.clearRect(0, 0, w, h);
      const cols = Math.ceil(w / spacing) + 1;
      const rows = Math.ceil(h / spacing) + 1;
      const cx = w / 2;
      const cy = h / 2;
      const maxDist = Math.sqrt(cx * cx + cy * cy);

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = i * spacing;
          const y = j * spacing;
          const dist = Math.hypot(x - cx, y - cy);
          const wave = Math.sin((dist / maxDist) * Math.PI * 4 - progress) * 0.5 + 0.5;
          const opacity = 0.07 + wave * 0.22;
          const useAccent = (i + j) % accentEvery === 0;
          ctx.fillStyle = useAccent
            ? `rgba(${accent},${(opacity * 0.7).toFixed(3)})`
            : `rgba(${cyan},${opacity.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(x, y, dotSize, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (!reduceMotion) {
        progress += speed;
        raf = requestAnimationFrame(draw);
      }
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [spacing, dotSize, cyan, accent, accentEvery, speed]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        display: 'block',
      }}
    />
  );
}
